/**
 * „Usunąć ćwiczenie?" and, when the backend refuses, „Ćwiczenie w użyciu" — one prompt in two
 * phases, over `shared/ui/ConfirmDialog`.
 *
 * Every sentence is the PHONE's (`exerciseLibrary.delete.*`), because a trainer meets the same wall
 * on two devices and should read the same words. The confirm is DESTRUCTIVE — `--danger` fill, no
 * ember — and Cancel is first in the DOM, so Enter on reflex deletes nothing.
 *
 * The refusal is the same dialog saying what happened rather than a second one stacked over it: the
 * trainer asked one question and gets one answer, and both of its buttons close it. A refusal names
 * no counts — the 409 carries none (`features/create-exercise`'s `deleteOutcome`).
 *
 * It is rendered INSIDE the theatre's popup, so Base UI treats it as a nested dialog: Escape answers
 * this prompt and leaves the card, and focus comes back to the ⋯ that raised it.
 */

import { useTranslation } from 'react-i18next';

import { exerciseInUseMessage, type ExerciseInUseRefusal } from '~/features/create-exercise';
import { ConfirmDialog, type ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import type { ExerciseActions } from '../lib/useExerciseActions';

export function DeleteExerciseConfirm({
  actions,
  finalFocus,
}: {
  readonly actions: ExerciseActions;
  /** The ⋯ that raised it. After a successful delete the card closes and chooses for itself. */
  readonly finalFocus: React.RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const prompt = actions.deletePrompt;

  return (
    <ConfirmDialog
      prompt={
        prompt === null
          ? null
          : prompt.phase === 'inUse'
            ? inUsePrompt(prompt.refusal, prompt.exercise.name, (key, values) => t(key, values), {
                cancel: t('exerciseLibrary.delete.cancelCta'),
                close: t('common.close'),
              })
            : {
                // Asking, or asking again after a failure: the same question and the same „Usuń”,
                // with the phone's „spróbuj ponownie” in place of the explanation once it has failed.
                title: t('exerciseLibrary.delete.confirmTitle'),
                description:
                  prompt.phase === 'failed'
                    ? t('exerciseLibrary.delete.genericError')
                    : t('exerciseLibrary.delete.confirmBody', {
                        exerciseName: prompt.exercise.name,
                      }),
                // The phone's confirmation lists what goes with the exercise, one item per line.
                multiline: true,
                cancelLabel: t('exerciseLibrary.delete.cancelCta'),
                confirmLabel: t('exerciseLibrary.delete.confirmCta'),
                destructiveConfirm: true,
                busy: actions.deleting,
              }
      }
      onConfirm={actions.confirmDelete}
      onCancel={actions.cancelDelete}
      finalFocus={finalFocus}
    />
  );
}

/**
 * The phone's in-use refusal (mobile #744): its composed title, and a body that names the live plans
 * one per line — so the description renders its breaks. Nothing to confirm: both answers close it,
 * as the in-use phase always has, and „Zamknij” is the one that names what it does.
 */
function inUsePrompt(
  refusal: ExerciseInUseRefusal,
  exerciseName: string,
  translate: (key: string, values?: Record<string, unknown>) => string,
  labels: { readonly cancel: string; readonly close: string },
): ConfirmPrompt {
  const message = exerciseInUseMessage(refusal, translate);
  return {
    title: message.title,
    subject: exerciseName,
    description: message.body,
    multiline: true,
    cancelLabel: labels.cancel,
    confirmLabel: labels.close,
  };
}

